// Per-contact encrypted message history.
//
// Storage moved from localStorage → IndexedDB. The IDB store gives us a much
// larger quota (localStorage caps at ~5 MB across the whole origin) and one
// consistent backend for all sensitive material.
//
// XSS in this same origin can still read both stores — moving to IDB is NOT
// a defence against attacker JS. Payload encryption (AES-GCM under the
// storage key) is what actually protects message bodies.
//
// Legacy localStorage rows are migrated lazily on first load.

import { openDB, idbGet, idbPut, idbDelete, idbClear, STORES, withLock } from "./idb"

const LS_PREFIX = "tg_msgs_"

// Chunked to avoid RangeError on mobile when bytes.length is large
// (e.g. encrypted history that includes file payloads). `String.fromCharCode(...bytes)`
// spreads each byte into a function argument, blowing the JS engine's arg-count limit.
function toBase64(bytes) {
    let binary = ""
    const CHUNK = 8192
    for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
    }
    return btoa(binary)
}

function fromBase64(str) {
    return Uint8Array.from(atob(str), c => c.charCodeAt(0))
}

async function migrateFromLocalStorage(db, contactId) {
    const lsKey = LS_PREFIX + contactId
    const stored = localStorage.getItem(lsKey)
    if (!stored) return null
    try {
        const blob = JSON.parse(stored)
        await idbPut(db, STORES.MESSAGES, String(contactId), blob)
        return blob
    } finally {
        localStorage.removeItem(lsKey)
    }
}

async function readEnvelope(db, contactId) {
    const found = await idbGet(db, STORES.MESSAGES, String(contactId))
    if (found) return found
    return migrateFromLocalStorage(db, contactId)
}

export async function loadMessages(contactId, storageKey) {
    const db = await openDB()
    const envelope = await readEnvelope(db, contactId)
    if (!envelope) return []
    try {
        const decrypted = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv: fromBase64(envelope.iv) },
            storageKey,
            fromBase64(envelope.data)
        )
        return JSON.parse(new TextDecoder().decode(decrypted))
    } catch {
        return []
    }
}

export async function saveMessages(contactId, messages, storageKey) {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        storageKey,
        new TextEncoder().encode(JSON.stringify(messages))
    )
    const db = await openDB()
    await idbPut(db, STORES.MESSAGES, String(contactId), {
        iv: toBase64(iv),
        data: toBase64(new Uint8Array(encrypted)),
    })
}

// ── Additive, serialized history append ───────────────────────
// saveMessages() overwrites the whole envelope (used by import). appendMessages()
// instead merges `additions` into the existing history under a per-contact lock,
// so concurrent writers (inbox pipeline, post-send, a second tab) can't
// lost-update each other. This is the durable-write path for the hot flows.

function parseTs(v) {
    const t = new Date(v).getTime()
    return Number.isNaN(t) ? 0 : t
}

function dedupKey(m) {
    return `${m.id}|${m.timestamp}`
}

// Stable order: ms timestamp first (NaN-safe), then id (numeric-aware) as tiebreak.
function compareMessages(x, y) {
    const dt = parseTs(x.timestamp) - parseTs(y.timestamp)
    if (dt !== 0) return dt
    return String(x.id).localeCompare(String(y.id), undefined, { numeric: true })
}

export function mergeDedupe(existing, additions) {
    const seen = new Set(existing.map(dedupKey))
    return [...existing, ...additions.filter(m => !seen.has(dedupKey(m)))].sort(compareMessages)
}

export async function appendMessages(contactId, additions, storageKey) {
    if (!additions?.length || !storageKey) return
    await withLock(`tg-msgs-${contactId}`, async () => {
        const existing = await loadMessages(contactId, storageKey)
        const merged = mergeDedupe(existing, additions)
        await saveMessages(contactId, merged, storageKey)
    })
}

export async function clearMessages(contactId) {
    const db = await openDB()
    await idbDelete(db, STORES.MESSAGES, String(contactId))
    localStorage.removeItem(LS_PREFIX + contactId)
}

export async function clearAllMessages() {
    const db = await openDB()
    await idbClear(db, STORES.MESSAGES)
    Object.keys(localStorage)
        .filter(k => k.startsWith(LS_PREFIX))
        .forEach(k => localStorage.removeItem(k))
}
